import { Trans, useLingui } from '@lingui/react/macro';

import type { ShiftSet } from '@/types/template';

/**
 * Tooltip body for a shift pill in the template preview.
 *
 * Heading shows the user-given shift name (when set) on the first line
 * and the shift type with its time range underneath. Below the heading
 * a "Staffing requirements" section colour-codes the staffing method
 * and — for algorithm-assigned shifts — lists the applicable groups
 * and tags (falling back to "All Groups" / "All Tags" when none are
 * set). For fixed shifts the assigned employee name is shown when
 * provided.
 */
export function ShiftTooltipContent({ shift, assignedEmployee }: ShiftTooltipContentProps) {
  const { t } = useLingui();
  const isAlgorithm = shift.staffing.method === 'automatic';
  const staffGroups =
    shift.staffing.method === 'specific' ? [] : (shift.staffing.filters?.staffGroups ?? []);
  const tags = shift.staffing.method === 'specific' ? [] : (shift.staffing.filters?.tags ?? []);
  const shiftName = (shift.name ?? '').trim();
  const typeName = shift.shiftType.name.trim();
  // Heading: shift.name on top, type+time as subtitle. When the shift has
  // no name at all (legacy data), promote the type to the heading, and if
  // the type is also missing fall back to a placeholder so the tooltip
  // never collapses to whitespace + `(HH:MM–HH:MM)`.
  const heading = shiftName || typeName || t`Untitled shift`;
  const showSubtitle = shiftName !== '' && typeName !== '';

  return (
    <div className="space-y-2 text-xs">
      <div>
        <div className="text-sm font-semibold">{heading}</div>
        <div className="text-gray-300">
          {showSubtitle && `${typeName} `}({shift.startTime}–{shift.endTime})
        </div>
      </div>

      <div className="border-t border-gray-600 pt-2">
        <div className="text-muted-foreground mb-1 font-medium">
          <Trans>Staffing requirements:</Trans>
        </div>

        {shift.staffing.method === 'open' && (
          <div className="mt-1 font-medium text-orange-400">
            <Trans>Open shift</Trans>
          </div>
        )}
        {isAlgorithm && (
          <div className="mt-1 font-medium text-purple-400">
            <Trans>Auto-assigned</Trans>
          </div>
        )}
        {shift.staffing.method === 'specific' && (
          <div className="mt-1 font-medium text-blue-400">
            <Trans>Specific employee</Trans>
          </div>
        )}

        {isAlgorithm && (
          <>
            <div className="mt-1">
              <span className="text-gray-400">
                <Trans>Groups:</Trans>
              </span>{' '}
              {staffGroups.length > 0
                ? staffGroups.map((group) => group.name).join(', ')
                : 'All Groups'}
            </div>
            <div className="mt-1">
              <span className="text-gray-400">
                <Trans>Tags:</Trans>
              </span>{' '}
              {tags.length > 0 ? tags.map((tag) => tag.name).join(', ') : 'All Tags'}
            </div>
          </>
        )}

        {assignedEmployee && (
          <div className="mt-1">
            <span className="text-gray-400">
              <Trans>Assigned to:</Trans>
            </span>{' '}
            {assignedEmployee}
          </div>
        )}

        {!assignedEmployee &&
          staffGroups.length === 0 &&
          tags.length === 0 &&
          shift.staffing.method !== 'open' &&
          !isAlgorithm && (
            <div className="mt-1 text-gray-400">
              <Trans>All employees</Trans>
            </div>
          )}
      </div>
    </div>
  );
}

interface ShiftTooltipContentProps {
  /** The shift to summarise. */
  shift: ShiftSet;
  /** Employee name to display for fixed-assignment shifts. */
  assignedEmployee?: string;
}
